"use client"

import { useEffect, useRef, useState } from "react"
import { Meeting } from "@/schema"
import { Skeleton, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/shad"
import { Check, Clock, ContactRound, Link2, Tags, X } from "lucide-react"

import { useFetchFeatureFlags } from "@/hooks/api/feature-flags/queries"
import { useUpdateMeeting } from "@/hooks/api/meetings"
import { MeetingUnknownParticipants } from "@/components/meetings/meeting-participant-add-menu"
import MeetingTopicSelector from "@/components/meetings/meeting-topic-selector"

import AssociationsSelector from "./associations-selector"
import MeetingAttachments from "./meeting-attachments"
import MeetingDateTimeEditor from "./meeting-date-time-editor"

export default function MeetingHeader({ meeting, isLoading }: { meeting?: Meeting; isLoading: boolean }) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedTitle, setEditedTitle] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const updateMeeting = useUpdateMeeting()
  const featureFlags = useFetchFeatureFlags()
  const meetingTopicsEnabled = featureFlags.data?.includes("meeting_topics") ?? false

  const title = meeting?.title || "Untitled meeting"

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleEditClick = () => {
    setEditedTitle(title || "Untitled meeting")
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (!meeting || editedTitle.trim() === (title || "")) {
      setIsEditing(false)
      return
    }

    try {
      await updateMeeting.mutateAsync({
        meetingId: meeting.id,
        data: { title: editedTitle.trim() || undefined },
      })
      setIsEditing(false)
    } catch {
      setEditedTitle(title || "Untitled meeting")
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setEditedTitle(title || "Untitled meeting")
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSave()
    } else if (e.key === "Escape") {
      e.preventDefault()
      handleCancel()
    }
  }

  const unknownParticipants = meeting?.unknown_participants ?? []

  return (
    <div className="flex w-full flex-col">
      <div className="flex h-6 items-center space-x-2">
        {isLoading ? (
          <Skeleton className="h-8 w-48" />
        ) : isEditing ? (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              className="ring-border focus:ring-ring min-w-64 rounded border-0 bg-transparent px-1 py-0 text-lg/tight font-semibold ring-1 outline-none"
              style={{ width: `${Math.max(16, editedTitle.length * 0.6)}rem` }}
              disabled={updateMeeting.isPending}
            />
            <button
              onClick={handleSave}
              disabled={updateMeeting.isPending}
              className="hover:bg-muted rounded p-1 disabled:opacity-50"
              title="Save"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancel}
              disabled={updateMeeting.isPending}
              className="hover:bg-muted rounded p-1 disabled:opacity-50"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <h1
            className={`text-lg/tight font-semibold ${meeting ? "hover:text-muted-foreground -mx-1 cursor-pointer rounded px-1 transition-colors" : ""}`}
            onClick={meeting ? handleEditClick : undefined}
            title={meeting ? "Click to edit title" : undefined}
          >
            {title || "Untitled meeting"}
          </h1>
        )}
      </div>
      {meeting && !isLoading && (
        <div className="mt-1 flex flex-col gap-1">
          {meeting.start_time && (
            <div className="flex items-start gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="mt-0.5 flex cursor-help justify-center">
                      <Clock className="text-icon size-4" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left" sideOffset={6} align="center">
                    Date & time
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <MeetingDateTimeEditor
                meeting={meeting}
                displayFormat="long"
                showEndTime={false}
                textClassName="text-secondary-foreground pl-1 text-sm"
              />
            </div>
          )}
          <div className="flex items-start gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="mt-0.5 flex cursor-help justify-center">
                    <Link2 className="text-icon size-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" sideOffset={6} align="center">
                  Associations
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <AssociationsSelector meeting={meeting} />
          </div>
          {meetingTopicsEnabled && (
            <div className="flex items-start gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="mt-0.5 flex cursor-help justify-center">
                      <Tags className="text-icon size-4" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left" sideOffset={6} align="center">
                    Meeting topics
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <MeetingTopicSelector meeting={meeting} />
            </div>
          )}
          {meeting.calendar_event_id && (
            <div className="flex items-start gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="mt-0.5 flex cursor-help justify-center">
                      <ContactRound className="text-icon size-4" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left" sideOffset={6} align="center">
                    Other attendees
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <MeetingUnknownParticipants
                participants={unknownParticipants}
                emptyLabel="No other attendees"
                meetingId={meeting?.id}
                className="inline-flex"
              />
            </div>
          )}
          <MeetingAttachments meeting={meeting} />
        </div>
      )}
    </div>
  )
}
